import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

/**
 * Pending action to be synced when back online
 */
export interface PendingAction {
  id: string
  type: 'create' | 'update' | 'delete'
  entity: string
  endpoint: string
  data: unknown
  timestamp: number
}

/**
 * Database schema for offline storage
 */
interface OfflineDBSchema extends DBSchema {
  projects: {
    key: number
    value: Record<string, unknown>
    indexes: { 'by-code': string }
  }
  lots: {
    key: number
    value: Record<string, unknown>
    indexes: { 'by-project': number }
  }
  quotes: {
    key: number
    value: Record<string, unknown>
    indexes: { 'by-project': number }
  }
  invoices: {
    key: number
    value: Record<string, unknown>
    indexes: { 'by-project': number }
  }
  activities: {
    key: number
    value: Record<string, unknown>
    indexes: { 'by-lot': number }
  }
  customers: {
    key: number
    value: Record<string, unknown>
  }
  suppliers: {
    key: number
    value: Record<string, unknown>
  }
  products: {
    key: number
    value: Record<string, unknown>
  }
  documents: {
    key: number
    value: Record<string, unknown>
  }
  pendingActions: {
    key: string
    value: PendingAction
    indexes: { 'by-timestamp': number }
  }
  metadata: {
    key: string
    value: { key: string; lastSync: number; version: number }
  }
}

const DB_NAME = 'ksproject-offline'
const DB_VERSION = 1

let dbInstance: IDBPDatabase<OfflineDBSchema> | null = null

/**
 * Get or create the database instance
 */
async function getDB(): Promise<IDBPDatabase<OfflineDBSchema>> {
  if (dbInstance) return dbInstance

  dbInstance = await openDB<OfflineDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Projects store
      if (!db.objectStoreNames.contains('projects')) {
        const projectStore = db.createObjectStore('projects', { keyPath: 'id' })
        projectStore.createIndex('by-code', 'code')
      }

      // Lots store
      if (!db.objectStoreNames.contains('lots')) {
        const lotStore = db.createObjectStore('lots', { keyPath: 'id' })
        lotStore.createIndex('by-project', 'projectId')
      }

      // Quotes store
      if (!db.objectStoreNames.contains('quotes')) {
        const quoteStore = db.createObjectStore('quotes', { keyPath: 'id' })
        quoteStore.createIndex('by-project', 'projectId')
      }

      // Invoices store
      if (!db.objectStoreNames.contains('invoices')) {
        const invoiceStore = db.createObjectStore('invoices', { keyPath: 'id' })
        invoiceStore.createIndex('by-project', 'projectId')
      }

      // Activities store
      if (!db.objectStoreNames.contains('activities')) {
        const activityStore = db.createObjectStore('activities', { keyPath: 'id' })
        activityStore.createIndex('by-lot', 'lotId')
      }

      // Customers store
      if (!db.objectStoreNames.contains('customers')) {
        db.createObjectStore('customers', { keyPath: 'id' })
      }

      // Suppliers store
      if (!db.objectStoreNames.contains('suppliers')) {
        db.createObjectStore('suppliers', { keyPath: 'id' })
      }

      // Products store
      if (!db.objectStoreNames.contains('products')) {
        db.createObjectStore('products', { keyPath: 'id' })
      }

      // Documents store
      if (!db.objectStoreNames.contains('documents')) {
        db.createObjectStore('documents', { keyPath: 'id' })
      }

      // Pending actions store
      if (!db.objectStoreNames.contains('pendingActions')) {
        const pendingStore = db.createObjectStore('pendingActions', { keyPath: 'id' })
        pendingStore.createIndex('by-timestamp', 'timestamp')
      }

      // Metadata store
      if (!db.objectStoreNames.contains('metadata')) {
        db.createObjectStore('metadata', { keyPath: 'key' })
      }
    }
  })

  return dbInstance
}

export type StoreName = 'projects' | 'lots' | 'quotes' | 'invoices' | 'activities' | 'customers' | 'suppliers' | 'products' | 'documents'

/**
 * Offline storage utility for IndexedDB operations
 */
export const offlineStorage = {
  /**
   * Get all items from a store
   */
  async getAll<T>(storeName: StoreName): Promise<T[]> {
    const db = await getDB()
    return db.getAll(storeName) as Promise<T[]>
  },

  /**
   * Get a single item by ID
   */
  async get<T>(storeName: StoreName, id: number): Promise<T | undefined> {
    const db = await getDB()
    return db.get(storeName, id) as Promise<T | undefined>
  },

  /**
   * Save a single item
   */
  async save<T extends { id: number }>(storeName: StoreName, item: T): Promise<void> {
    const db = await getDB()
    await db.put(storeName, item as Record<string, unknown>)
  },

  /**
   * Save multiple items (replaces all existing)
   */
  async saveAll<T extends { id: number }>(storeName: StoreName, items: T[]): Promise<void> {
    const db = await getDB()
    const tx = db.transaction(storeName, 'readwrite')
    await tx.store.clear()
    await Promise.all(items.map(item => tx.store.put(item as Record<string, unknown>)))
    await tx.done
  },

  /**
   * Delete an item by ID
   */
  async delete(storeName: StoreName, id: number): Promise<void> {
    const db = await getDB()
    await db.delete(storeName, id)
  },

  /**
   * Clear all items from a store
   */
  async clear(storeName: StoreName): Promise<void> {
    const db = await getDB()
    await db.clear(storeName)
  },

  /**
   * Add a pending action to be synced later
   */
  async addPendingAction(action: Omit<PendingAction, 'id' | 'timestamp'>): Promise<string> {
    const db = await getDB()
    const pendingAction: PendingAction = {
      ...action,
      id: crypto.randomUUID(),
      timestamp: Date.now()
    }
    await db.add('pendingActions', pendingAction)
    return pendingAction.id
  },

  /**
   * Get all pending actions sorted by timestamp
   */
  async getPendingActions(): Promise<PendingAction[]> {
    const db = await getDB()
    return db.getAllFromIndex('pendingActions', 'by-timestamp')
  },

  /**
   * Get count of pending actions
   */
  async getPendingActionsCount(): Promise<number> {
    const db = await getDB()
    return db.count('pendingActions')
  },

  /**
   * Remove a pending action after successful sync
   */
  async removePendingAction(id: string): Promise<void> {
    const db = await getDB()
    await db.delete('pendingActions', id)
  },

  /**
   * Clear all pending actions
   */
  async clearPendingActions(): Promise<void> {
    const db = await getDB()
    await db.clear('pendingActions')
  },

  /**
   * Get last sync timestamp for a store
   */
  async getLastSync(storeName: StoreName): Promise<number | null> {
    const db = await getDB()
    const metadata = await db.get('metadata', storeName)
    return metadata?.lastSync || null
  },

  /**
   * Update last sync timestamp for a store
   */
  async setLastSync(storeName: StoreName): Promise<void> {
    const db = await getDB()
    await db.put('metadata', {
      key: storeName,
      lastSync: Date.now(),
      version: DB_VERSION
    })
  },

  /**
   * Clear all offline data
   */
  async clearAll(): Promise<void> {
    const db = await getDB()
    const storeNames: StoreName[] = ['projects', 'lots', 'quotes', 'invoices', 'activities', 'customers', 'suppliers', 'products', 'documents']
    await Promise.all(storeNames.map(name => db.clear(name)))
    await db.clear('pendingActions')
    await db.clear('metadata')
  },

  /**
   * Get metadata value by key
   */
  async getMetadata(key: string): Promise<number | null> {
    const db = await getDB()
    const metadata = await db.get('metadata', key)
    return metadata?.lastSync || null
  },

  /**
   * Set metadata value by key
   */
  async setMetadata(key: string, value: number | null): Promise<void> {
    const db = await getDB()
    if (value === null) {
      await db.delete('metadata', key)
    } else {
      await db.put('metadata', {
        key,
        lastSync: value,
        version: DB_VERSION
      })
    }
  }
}

export default offlineStorage
